import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/FirebaseService';
import { CircularProgress, Box, Typography, Paper } from '@mui/material';

/**
 * PrivateRoute component that checks if the user is authenticated and allowed to access the app
 * If not authenticated, redirects to the login page
 * If authenticated but not allowed, shows an access denied message
 */
const PrivateRoute = () => {
  const { currentUser, loading } = useAuth();
  const [isAllowed, setIsAllowed] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (currentUser) {
        try {
          const allowed = await firebaseService.checkCurrentUserAccess();
          setIsAllowed(allowed);
        } catch (error) {
          console.error('Error checking access:', error);
          setIsAllowed(false);
        }
      }
      setCheckingAccess(false);
    };

    if (!loading) {
      checkAccess();
    }
  }, [currentUser, loading]);

  if (loading || checkingAccess) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Checking access...
        </Typography>
      </Box>
    );
  }

  if (!currentUser) {
    return <Navigate to="/" />;
  }

  if (!isAllowed) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Paper sx={{ 
          p: 4, 
          maxWidth: 500, 
          textAlign: 'center',
          backgroundColor: '#1a1a1a'
        }}>
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your email address is not authorized to use this application.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please contact an administrator to request access, or sign in with an authorized account.
          </Typography>
          <Box sx={{ mt: 3 }}>
            <button 
              onClick={() => firebaseService.signOut()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Sign Out
            </button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return <Outlet />;
};

export default PrivateRoute; 